import { useTranslation } from 'react-i18next'
import { useGetInvestigationById } from '@/application/services/investigation.service'
import { useGetLeadById } from '@/application/services/lead.service'
import { Badge } from '@/ui/components/common/Badge'
import { Breadcrumb } from '@/ui/components/common/Breadcrumb'
import { MarkdownView } from '@/ui/components/common/MarkdownView'
import { PreviewArticleButton } from '@/ui/pages/management/investigations/PreviewArticleButton'

/**
 * Top of the lead-detail page: breadcrumb, investigation context block,
 * and the main lead "card" (badge + image + title + description + submitter).
 *
 * Owns: data fetching for the parent investigation and the lead via React Query
 * (the cache is shared with sibling sections, so refetches dedupe automatically).
 */
export function LeadHeaderSection({ id, leadId }: { id: string; leadId: string }) {
  const { t } = useTranslation()
  const { data: investigation } = useGetInvestigationById(id)
  const { data: lead } = useGetLeadById(leadId)

  if (!lead) return null

  return (
    <>
      {/* ─── Breadcrumb ─── */}
      <Breadcrumb
        items={[
          { label: t('admin.nav_investigations'), href: '/manage/investigations' },
          { label: investigation?.name ?? '…', href: `/manage/investigations/${id}/leads` },
          { label: lead.title || t('lead_detail.fallback_title') },
        ]}
      />

      {/* ─── Investigation context ─── */}
      {investigation && (
        <div className="flex items-start justify-between gap-4 pb-4 mb-2 border-b border-border">
          <div className="min-w-0">
            <h2 className="font-serif text-xl font-bold text-foreground leading-tight">
              {investigation.name}
            </h2>
            {investigation.description && (
              <MarkdownView
                content={investigation.description}
                className="text-sm text-muted-foreground mt-1 leading-relaxed"
              />
            )}
          </div>
          <PreviewArticleButton investigation={investigation} leadId={leadId} />
        </div>
      )}

      {/* ─── Lead card ─── */}
      <div className="bg-card border border-border rounded-lg overflow-hidden flex">
        {lead.imageUrl && (
          <img src={lead.imageUrl} alt={lead.title} className="w-48 shrink-0 object-cover" />
        )}

        <div className="border-l-4 border-primary p-6 flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-3">
            <Badge variant="outline" className="text-primary border-primary/30 text-xs">
              {t('lead_detail.badge')}
            </Badge>
            <span className="text-xs text-muted-foreground">
              {new Date(lead.createdAt).toLocaleDateString('fr-FR', {
                day: '2-digit',
                month: 'long',
                year: 'numeric',
              })}
            </span>
          </div>

          <h2 className="font-serif text-2xl font-bold mb-2">
            {lead.title || t('lead_detail.fallback_title')}
          </h2>
          {lead.description && (
            <MarkdownView
              content={lead.description}
              className="text-foreground/80 text-sm leading-relaxed mb-3"
            />
          )}

          <p className="text-xs text-muted-foreground">
            {t('common.submitted_by')}:{' '}
            <span className="font-medium text-foreground">{lead.submitterUsername}</span>
          </p>
        </div>
      </div>
    </>
  )
}
